import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";

import { getCurrentUserData } from "../services/settingsService/settingsService";
import { useAuth } from "./AuthContext";
import { logger } from "utils/logger";

type User = {
  firstName: string;
  lastName: string;
  email: string;
  organizationName?: string;
} | null;

interface UserContextValue {
  user: User;
  setUser: (user: User) => void;
  reloadUser: () => Promise<void>;
}

const UserContext = createContext<UserContextValue | null>(null);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, role } = useAuth();
  const [user, setUser] = useState<User>(null);

  const reloadUser = useCallback(async () => {
    if (role === "student") {
      setUser({
        firstName: Cookies.get("first_name") || "",
        lastName: Cookies.get("last_name") || "",
        email: Cookies.get("email") || "",
      });
      return;
    }

    if (role !== "teacher") {
      setUser(null);
      return;
    }

    try {
      const data = await getCurrentUserData();
      setUser({
        firstName: data.first_name || "Name",
        lastName: data.last_name || "Lastname",
        email: data.email || "",
        organizationName: data.organization_name || "",
      });
    } catch (error) {
      logger.error("Ошибка загрузки данных пользователя:", error);
      setUser(null);
    }
  }, [role]);

  useEffect(() => {
    if (!isAuthenticated) {
      setUser(null);
      return;
    }
    void reloadUser();
  }, [isAuthenticated, reloadUser]);

  return (
    <UserContext.Provider value={{ user, setUser, reloadUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used within UserProvider");
  return ctx;
};
